import {useEffect, useState} from 'react'
import {Box, Flex, SimpleGrid, Text} from '@chakra-ui/react'
import {FormProvider, useForm} from 'react-hook-form'
import {cloneDeep, isEmpty, round} from 'lodash-es'
import {useTranslation} from 'next-i18next'
import Range from '_app/components/Form/Range'
import Button from '_app/components/Buttons/Button'
import useAttributesMetadata from '_app/hooks/useAttributesMetadata'
import {ProductAttrKey} from '_app/types/product'
import {useRouter} from '_app/hooks/useRouter'
import {DropdownLayerFilter} from './FiltersDropdown'
import FilterItemsList from './FilterItemsList'
import FiltersFormCounter from './FiltersFormCounter'
import FilterEnum from '../../../enums/FilterEnum/FilterEnum'
import AttributesEnum from '../../../enums/AttributesEnum/AttributesEnum'
import URLHelper from '../../../helpers/URLHelper/URLHelper'

type LoadingState = 'clear' | 'submit' | null;

interface FiltersFormProps {
  defaultValues: { [key: string]: any };
  initialValues: { [key: string]: any };
  filters: DropdownLayerFilter[];
  minPrice: number;
  maxPrice: number;
  onClose: () => void;
}

const FiltersForm = ({
  defaultValues,
  initialValues,
  filters,
  minPrice,
  maxPrice,
  onClose,
}: FiltersFormProps) => {
  const { t } = useTranslation('listing')
  const router = useRouter()

  const [loadingState, setLoadingState] = useState<LoadingState>(null)

  const methods = useForm({
    defaultValues: cloneDeep(defaultValues),
  })

  const { reset, handleSubmit } = methods

  useEffect(() => {
    reset(cloneDeep(initialValues))
  }, [reset, initialValues])

  const onSubmit = async (data: { [key: string]: any }) => {
    setLoadingState('submit')
    const query: { [key: string]: any } = URLHelper.getSearchParams();
    [...AttributesEnum.FILTERS_ATTRIBUTE_CODES, 'p'].forEach(key => {
      delete query[key]
    })

    const { price, ...restValues } = data

    if (price && (price[0] !== minPrice || price[1] !== maxPrice)) {
      query.price = price.join('-')
    }

    Object.entries(restValues).map(([key, value]) => {
      if (!isEmpty(value)) {
        query[key] = value
      }
    })

    await router.search(query)

    setLoadingState(null)
    onClose()
  }

  const onClear = async () => {
    setLoadingState('clear')
    const query = URLHelper.getSearchParams()

    AttributesEnum.FILTERS_ATTRIBUTE_CODES.forEach(key => {
      delete query[key]
    })

    await router.search(query)

    reset(cloneDeep(defaultValues))

    setLoadingState(null)
    onClose()
  }

  const { metaData: attributes } = useAttributesMetadata({})

  return (
    <FormProvider {...methods}>
      <Box
        as="form"
        overflow="auto"
        px={2}
        mx={-2}
        onSubmit={handleSubmit(onSubmit)}
      >
        <SimpleGrid
          columns={{
            base: 1,
            lg: 4,
          }}
          spacing="50px"
          lineHeight="1.44"
        >
          {filters
            .filter(f =>
              FilterEnum.ALLOWED_FILTER_ATTRIBUTE_CODES.includes(f.attribute_code)
            )
            .map(({ label, attribute_code, options }) => (
              <Box key={`f-${attribute_code}`}>
                <Text
                  textStyle="body"
                  mb="16px"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {label}
                </Text>
                {attribute_code === 'price' ? (
                  <Range
                    name={attribute_code}
                    orientation="vertical"
                    minH="140px"
                    extend={40}
                    min={minPrice}
                    max={maxPrice}
                    minStepsBetweenThumbs={
                      round(
                        (defaultValues?.price[1] - defaultValues?.price[0]) / 5,
                        0
                      ) || 0
                    }
                  />
                ) : (
                  <FilterItemsList
                    requestVar={attribute_code}
                    items={options}
                    swatches={attributes[attribute_code as ProductAttrKey]}
                  />
                )}
              </Box>
            ))}
        </SimpleGrid>
        <Flex justifyContent="flex-end" mt="60px">
          <Button
            reversed
            onClick={onClear}
            isLoading={loadingState === 'clear'}
          >
            {t('CLEAR_FILTERS', { ns: 'common' })}
          </Button>
          <Button type="submit" isLoading={loadingState === 'submit'}>
            {t('APPLY', { ns: 'common' })}
            <FiltersFormCounter
              {...{
                minPrice,
                maxPrice,
              }}
            />
          </Button>
        </Flex>
      </Box>
    </FormProvider>
  )
}

export default FiltersForm
